import { computed, ref } from '@vue/composition-api';
import { useContext, useStore } from '@nuxtjs/composition-api';
import { differenceInCalendarDays, isValid } from 'date-fns';
import { formatInTimeZone } from 'date-fns-tz';

import { UserSubscription, UserSubscriptionStatus, UserSubscriptionType } from '~/data/types';
import { State } from '~/data/types/store';
import { convertDashesToSlashes, DateFormat } from '~/helpers/date';
import { TRIAL_EXPIRY_DAYS_SHOW_BANNER } from '~/constants';
import { getDeviceToken } from '~/data/auth/device-token';
import { useOnSuccess } from '~/data/auth/use-on-success';

type Card = {
  type: string,
  number: string,
};

export const useUserSubscription = () => {
  const { $auth } = useContext();
  const store = useStore<State>();
  const { onSuccess } = useOnSuccess();
  const isLoading = ref(false);
  const hasSubscription = computed<boolean>(() => !!store.state.auth.user.data.subscription);
  const subscription = computed<UserSubscription | null>(() => store.state.auth.user.data.subscription || null);
  const currentPaymentAmount = computed<string | null>(() => {
    if (!store.state.auth.user.data.subscription) {
      return null;
    }

    if (store.state.auth.user.data.subscription.payment_amount && store.state.auth.user.data.subscription.payment_interval) {
      return `$${store.state.auth.user.data.subscription.payment_amount}/${store.state.auth.user.data.subscription.payment_interval}`;
    }

    return store.state.auth.user.data.subscription?.stripe_plan_title || null;
  });
  const scheduledPaymentAmount = computed<string | null>(() => store.state.auth.user.data.subscription?.stripe_scheduled_plan_title || null); // TODO: Updated Stripe Details
  const nextPaymentDate = computed<string | null>(() => {
    if (store.state.auth.user.data.subscription?.next_payment_date) {
      const nextPaymentDate = new Date(convertDashesToSlashes(store.state.auth.user.data.subscription.next_payment_date));
      return isValid(nextPaymentDate) ? formatInTimeZone(nextPaymentDate, 'UTC', DateFormat.Human) : null;
    }
    return null;
  });
  const cardDetails = computed<Card | null>(() => {
    if (!!store.state.auth.user.data.card_brand && !!store.state.auth.user.data.card_last_four) {
      return {
        type: store.state.auth.user.data.card_brand,
        number: store.state.auth.user.data.card_last_four,
      };
    }
    return null;
  });
  const hasDiscount = computed(() => {
    if (!subscription.value || !currentPaymentAmount.value) {
      return null;
    }

    return subscription.value.stripe_plan_title !== currentPaymentAmount.value;
  });

  const hasTrialingStatus = computed(() => store.state.auth.user?.data?.subscription?.status === UserSubscriptionStatus.Trialing);
  const hasCardDetails = computed(() => !!store.state.auth.user?.data?.card_last_four && !!store.state.auth.user?.data?.card_brand);
  const hasStripeSubscription = computed(() => store.state.auth.user?.data?.subscription?.type === UserSubscriptionType.Stripe);
  const hasAppleIAPSubscription = computed(() => store.state.auth.user?.data?.subscription?.type === UserSubscriptionType.AppleIAP);
  const isTrialing = computed(() => hasTrialingStatus.value && !hasCardDetails.value && hasStripeSubscription.value);
  const isTrialingLegacyIOS = computed<boolean>(() => hasTrialingStatus.value && !hasCardDetails.value && hasAppleIAPSubscription.value); // TODO: Remove once remove paywall has been released
  const isTrialingLegacyWeb = computed<boolean>(() => hasTrialingStatus.value && hasCardDetails.value && hasStripeSubscription.value); // TODO: Remove once remove paywall has been released
  const isActive = computed(() => store.state.auth.user?.data?.subscription?.status === UserSubscriptionStatus.Active
    || store.state.auth.user?.data?.subscription?.status === UserSubscriptionStatus.PastDue);
  const isUnsubscribed = computed<boolean>(() => store.getters['user/isUnsubscribed']);

  const trialExpiryDays = computed(() => {
    if ((isTrialing.value || isTrialingLegacyIOS.value || isTrialingLegacyWeb.value) && !!store.state.auth.user.data.trial_ends_at) {
      const trialEndsAt = new Date(convertDashesToSlashes(store.state.auth.user.data.trial_ends_at));
      const date = new Date(formatInTimeZone(new Date(), 'UTC', DateFormat.DateTime));
      return differenceInCalendarDays(trialEndsAt, date);
    }

    return null;
  });
  const trialExpiresToday = computed(() => trialExpiryDays.value != null && trialExpiryDays.value <= 0);
  const trialExpiryDaysHuman = computed(() => {
    if (trialExpiryDays.value == null) {
      return null;
    }

    return trialExpiresToday.value
      ? 'today'
      : `${trialExpiryDays.value} day${trialExpiryDays.value === 1 ? '' : 's'}`;
  });

  const shouldHideTrialCTA = computed(() => store.state.subscription.hideTrialCTA);
  const hasTrialEndingSoon = computed(() => !shouldHideTrialCTA.value
    && (trialExpiryDays.value ?? -1) >= 0
    && (trialExpiryDays.value ?? -1) <= TRIAL_EXPIRY_DAYS_SHOW_BANNER);

  const changePaymentMethod = async(token: string): Promise<boolean> => {
    isLoading.value = true;
    try {
      const response = await store.dispatch({
        type: 'user/changeSettings',
        endpoint: 'user-subscription/change-payment-method',
        data: {
          card_token: token,
        },
      });
      if (response?.statusCode === 200 && response?.data?.id) {
        $auth.setUser(response);
        return true;
      }
    } catch {
      // Do nothing
    }

    isLoading.value = false;
    return false;
  };

  const changePlan = async(planId: string, discountCode: string | null): Promise<boolean> => {
    isLoading.value = true;

    try {
      const response = await store.dispatch({
        type: 'user/changeSettings',
        endpoint: 'user-subscription/change-plan',
        data: {
          plan_id: planId,
          discount_code: discountCode,
        },
      });
      if (response?.statusCode === 200 && response?.data?.id) {
        $auth.setUser(response);
        return true;
      }
    } catch {
      // Do nothing
    }

    isLoading.value = false;
    return false;
  };

  const cancel = async(): Promise<boolean> => {
    isLoading.value = true;

    try {
      const response = await store.dispatch({
        type: 'user/changeSettings',
        endpoint: 'user-subscription/cancel',
      });
      if (response?.statusCode === 200 && response?.data?.id) {
        $auth.$storage.setCookie('subscription', 'canceled');
        $auth.setUser(response);
        return true;
      }
    } catch {
      // Do nothing
    }

    isLoading.value = false;
    return false;
  };

  const rejoin = async(token: string, planId: string, discountCode: string): Promise<boolean> => {
    isLoading.value = true;

    try {
      const response = await store.dispatch({
        type: 'user/changeSettings',
        endpoint: 'user-subscription/resubscribe',
        data: {
          card_token: token,
          discount_code: discountCode,
          plan_id: planId,
        },
      });
      if (response?.statusCode === 200 && response?.data?.id) {
        $auth.setUser(response);
        return true;
      }
    } catch {
      // Do nothing
    }

    isLoading.value = false;
    return false;
  };

  const upgradeFromTrial = async(token: string, planId: string, discountCode: string): Promise<boolean> => {
    isLoading.value = true;

    try {
      const response = await store.dispatch('user/upgradeFromTrial', {
        card_token: token,
        discount_code: discountCode,
        plan_id: planId,
        platform: 'web',
        device_token: getDeviceToken(),
      });
      if (response?.data?.statusCode === 200 && response?.data?.data?.id) {
        const shouldRememberMe = !!$auth.$storage.getCookie('_token_remember.local');
        await onSuccess(response, shouldRememberMe);
        return true;
      }
    } catch {
      // Do nothing
    }

    isLoading.value = false;
    return false;
  };

  const hideTrialCTA = () => {
    store.dispatch('subscription/hideTrialCTA');
  };

  return {
    cancel,
    cardDetails,
    changePaymentMethod,
    changePlan,
    currentPaymentAmount,
    hasAppleIAPSubscription,
    hasDiscount,
    hasStripeSubscription,
    hasSubscription,
    hasTrialEndingSoon,
    hideTrialCTA,
    isActive,
    isLoading,
    isTrialing,
    isTrialingLegacyIOS,
    isTrialingLegacyWeb,
    isUnsubscribed,
    nextPaymentDate,
    rejoin,
    scheduledPaymentAmount,
    shouldHideTrialCTA,
    subscription,
    trialExpiresToday,
    trialExpiryDays,
    trialExpiryDaysHuman,
    upgradeFromTrial,
  };
};
